import React, { useMemo } from 'react';
import {
    ResponsiveContainer,
    ComposedChart,
    Line,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Label,
    Scatter,
    ErrorBar
} from 'recharts';
import { ChartConfig, ScientificChartConfig } from '../../../../types';
import { DEFAULT_SCIENTIFIC_CONFIG, LINE_DASH_ARRAYS, SCIENTIFIC_COLOR_PALETTES } from '../config/scientificDefaults';
import { ScientificMarker } from './ScientificMarker';

interface ScientificLinePlotProps {
    config: ChartConfig;
    scientificConfig: ScientificChartConfig;
    data: any[];
    onPointClick?: (point: any) => void;
}

/**
 * Scientific Line Plot
 * Matplotlib-style line plot with curve smoothing, confidence intervals, and multiple series
 */
export const ScientificLinePlot: React.FC<ScientificLinePlotProps> = ({
    config,
    scientificConfig,
    data,
    onPointClick
}) => {
    const { fieldMappings, axisOptions, style, legend, animation, analysis, errorBars } = scientificConfig;
    const chartColor = config.customization?.color || '#f59e0b';

    // Series colors (WeaverBI-themed palette)
    const seriesColors = useMemo(() => {
        return ['#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
    }, []);


    // Get color from colormap
    const getColorFromPalette = (norm: number): string => {
        // Default to weaverGold palette for WeaverBI theme
        const palette = SCIENTIFIC_COLOR_PALETTES[style.colormap as keyof typeof SCIENTIFIC_COLOR_PALETTES] || SCIENTIFIC_COLOR_PALETTES.weaverGold;
        const idx = Math.min(Math.floor(norm * (palette.length - 1)), palette.length - 1);
        return palette[Math.max(0, idx)];
    };

    // Process data for line chart
    const { processedData, seriesKeys, hasConfidenceInterval, colorMin, colorMax } = useMemo(() => {
        if (!data || data.length === 0 || !fieldMappings.xField || !fieldMappings.yField) {
            return { processedData: [], seriesKeys: [], hasConfidenceInterval: false, colorMin: 0, colorMax: 1 };
        }

        const hasCI = !!(fieldMappings.yUpperBoundField && fieldMappings.yLowerBoundField);
        const hasSeries = !!fieldMappings.seriesField;
        const hasColor = !!fieldMappings.colorField;

        // Calculate color range if colorField is set
        let cMin = 0, cMax = 1;
        if (hasColor) {
            const colorVals = data.map(d => Number(d[fieldMappings.colorField!])).filter(v => !isNaN(v));
            if (colorVals.length > 0) {
                cMin = Math.min(...colorVals);
                cMax = Math.max(...colorVals);
            }
        }

        // Group data by x value (for multi-series) or just process directly
        const dataMap = new Map<any, any>();
        const uniqueSeries = new Set<string>();

        data.forEach(row => {
            const xVal = row[fieldMappings.xField!];
            if (xVal === undefined) return;

            const seriesKey = hasSeries ? String(row[fieldMappings.seriesField!]) : 'default';
            uniqueSeries.add(seriesKey);

            if (!dataMap.has(xVal)) {
                dataMap.set(xVal, { x: xVal, _xOriginal: xVal });
            }

            const entry = dataMap.get(xVal)!;

            // Set y value for this series
            const yKey = hasSeries ? `y_${seriesKey}` : 'y';
            entry[yKey] = row[fieldMappings.yField!];

            // Store color value if present
            if (hasColor) {
                const colorVal = Number(row[fieldMappings.colorField!]);
                entry[`color_${seriesKey}`] = colorVal;
                entry.colorNorm = (colorVal - cMin) / (cMax - cMin || 1);
            }

            // Confidence interval bounds
            if (hasCI) {
                entry.yUpper = row[fieldMappings.yUpperBoundField!];
                entry.yLower = row[fieldMappings.yLowerBoundField!];
            }

            // Calculate error bar values if enabled
            if (errorBars?.show && errorBars.mode !== 'none') {
                const yVal = row[fieldMappings.yField!];
                if (errorBars.mode === 'constant') {
                    const constantVal = errorBars.constantValue || 1;
                    entry.errorY = [constantVal, constantVal];
                } else if (errorBars.mode === 'field' && fieldMappings.yErrorField) {
                    const errorVal = Math.abs(Number(row[fieldMappings.yErrorField]) || 0);
                    entry.errorY = [errorVal, errorVal];
                }
            }

            // Keep original row reference
            entry._rows = entry._rows || {};
            entry._rows[seriesKey] = row;
        });

        // Sort by x value
        const processed = Array.from(dataMap.values()).sort((a, b) => {
            if (typeof a.x === 'number' && typeof b.x === 'number') return a.x - b.x;
            return String(a.x).localeCompare(String(b.x));
        });

        return {
            processedData: processed,
            seriesKeys: Array.from(uniqueSeries),
            hasConfidenceInterval: hasCI,
            colorMin: cMin,
            colorMax: cMax
        };
    }, [data, fieldMappings]);

    // Calculate Trendline coefficients and add to processedData
    const { dataWithTrend, hasTrendline } = useMemo(() => {
        const type = analysis?.trendline || 'none';
        if (type === 'none' || processedData.length < 2) {
            return { dataWithTrend: processedData, hasTrendline: false };
        }

        // Get first series data for trendline
        const dataKey = seriesKeys.length > 1 ? `y_${seriesKeys[0]}` : 'y';

        const validPoints = processedData.filter(d =>
            typeof d.x === 'number' && typeof d[dataKey] === 'number' && isFinite(d.x) && isFinite(d[dataKey])
        );

        if (validPoints.length < 2) {
            return { dataWithTrend: processedData, hasTrendline: false };
        }

        const xSeries = validPoints.map(d => d.x);
        const ySeries = validPoints.map(d => d[dataKey]);
        const n = validPoints.length;

        let getTrendValue: (x: number) => number;

        // Simple Linear Regression
        if (type === 'linear') {
            const sumX = xSeries.reduce((a, b) => a + b, 0);
            const sumY = ySeries.reduce((a, b) => a + b, 0);
            const sumXY = validPoints.reduce((acc, d) => acc + d.x * d[dataKey], 0);
            const sumXX = xSeries.reduce((a, b) => a + b * b, 0);

            const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
            const intercept = (sumY - slope * sumX) / n;

            getTrendValue = (x: number) => slope * x + intercept;
        }
        // Exponential Regression
        else if (type === 'exp') {
            const validExp = validPoints.filter(d => d[dataKey] > 0);
            if (validExp.length < 2) {
                return { dataWithTrend: processedData, hasTrendline: false };
            }

            const sumX = validExp.reduce((a, b) => a + b.x, 0);
            const sumYLn = validExp.reduce((a, b) => a + Math.log(b[dataKey]), 0);
            const sumXYLn = validExp.reduce((acc, d) => acc + d.x * Math.log(d[dataKey]), 0);
            const sumXX = validExp.reduce((a, b) => a + b.x * b.x, 0);
            const nExp = validExp.length;

            const slope = (nExp * sumXYLn - sumX * sumYLn) / (nExp * sumXX - sumX * sumX);
            const interceptLn = (sumYLn - slope * sumX) / nExp;
            const a = Math.exp(interceptLn);

            getTrendValue = (x: number) => a * Math.exp(slope * x);
        }
        // Polynomial (2nd degree)
        else if (type === 'poly2') {
            let sumX = 0, sumX2 = 0, sumX3 = 0, sumX4 = 0;
            let sumY = 0, sumXY = 0, sumX2Y = 0;

            validPoints.forEach(d => {
                const x = d.x;
                const y = d[dataKey];
                const x2 = x * x;

                sumX += x;
                sumX2 += x2;
                sumX3 += x2 * x;
                sumX4 += x2 * x2;
                sumY += y;
                sumXY += x * y;
                sumX2Y += x2 * y;
            });

            const det = n * (sumX2 * sumX4 - sumX3 * sumX3)
                - sumX * (sumX * sumX4 - sumX3 * sumX2)
                + sumX2 * (sumX * sumX3 - sumX2 * sumX2);

            if (Math.abs(det) < 1e-10) {
                const linearSlope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
                const linearIntercept = (sumY - linearSlope * sumX) / n;
                getTrendValue = (x: number) => linearSlope * x + linearIntercept;
            } else {
                const detC = sumY * (sumX2 * sumX4 - sumX3 * sumX3)
                    - sumX * (sumXY * sumX4 - sumX3 * sumX2Y)
                    + sumX2 * (sumXY * sumX3 - sumX2 * sumX2Y);

                const detB = n * (sumXY * sumX4 - sumX3 * sumX2Y)
                    - sumY * (sumX * sumX4 - sumX3 * sumX2)
                    + sumX2 * (sumX * sumX2Y - sumXY * sumX2);

                const detA = n * (sumX2 * sumX2Y - sumXY * sumX3)
                    - sumX * (sumX * sumX2Y - sumXY * sumX2)
                    + sumY * (sumX * sumX3 - sumX2 * sumX2);

                const polyC = detC / det;
                const polyB = detB / det;
                const polyA = detA / det;

                getTrendValue = (x: number) => polyA * x * x + polyB * x + polyC;
            }
        } else {
            return { dataWithTrend: processedData, hasTrendline: false };
        }

        // Add trend values to all data points
        const dataWithTrend = processedData.map(d => ({
            ...d,
            trend: typeof d.x === 'number' ? getTrendValue(d.x) : null
        }));

        return { dataWithTrend, hasTrendline: true };
    }, [processedData, analysis?.trendline, seriesKeys]);

    // Get line dash array
    const getDashArray = (): string => {
        return LINE_DASH_ARRAYS[style.lineStyle] || '';
    };

    // Axis formatter
    const formatAxis = (value: number): string => {
        if (typeof value !== 'number') return String(value);
        if (axisOptions.axisFormat === 'scientific') {
            return value.toExponential(2);
        } else if (axisOptions.axisFormat === 'engineering') {
            const exp = Math.floor(Math.log10(Math.abs(value)) / 3) * 3;
            if (exp === 0 || !isFinite(exp)) return value.toFixed(2);
            return `${(value / Math.pow(10, exp)).toFixed(2)}e${exp}`;
        }
        return typeof value === 'number' ? value.toLocaleString() : String(value);
    };

    // Animation style
    const animationStyle = animation.fadeIn ? {
        animation: `fadeIn ${animation.duration}ms ease-out`
    } : {};

    if (processedData.length === 0) {
        return (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
                <div className="text-center">
                    <p>No data to display</p>
                    <p className="text-sm text-gray-500 mt-1">Map X and Y fields to create a line plot</p>
                </div>
            </div>
        );
    }

    const isXNumeric = processedData.length > 0 && typeof processedData[0].x === 'number';

    // Calculate domains with padding
    const calculateDomain = (axis: 'x' | 'y'): [number, number] => {
        if (!processedData.length) return [0, 100];

        // For multi-series line chart, we need to adhere to min/max of ALL series
        let min = Infinity;
        let max = -Infinity;
        const isLog = axis === 'x' ? axisOptions.logScaleX : axisOptions.logScaleY;

        if (axis === 'x') {
            const values = processedData
                .map(d => d.x)
                .filter(v => typeof v === 'number' && Number.isFinite(v) && (!isLog || v > 0));

            if (values.length > 0) {
                min = Math.min(...values);
                max = Math.max(...values);
            }
        } else {
            // Check all series keys for Y axis
            processedData.forEach(d => {
                seriesKeys.forEach(key => {
                    const k = seriesKeys.length > 1 ? `y_${key}` : 'y';
                    const val = d[k];
                    if (typeof val === 'number' && Number.isFinite(val) && (!isLog || val > 0)) {
                        min = Math.min(min, val);
                        max = Math.max(max, val);
                    }
                });
                // Include CI bounds if present
                if (hasConfidenceInterval) {
                    if (typeof d.yLower === 'number' && Number.isFinite(d.yLower) && (!isLog || d.yLower > 0)) min = Math.min(min, d.yLower);
                    if (typeof d.yUpper === 'number' && Number.isFinite(d.yUpper) && (!isLog || d.yUpper > 0)) max = Math.max(max, d.yUpper);
                }
            });
        }

        if (!isFinite(min) || !isFinite(max)) {
            return isLog ? [0.1, 100] : [0, 100];
        }

        if (min === max) {
            if (isLog) {
                return [min * 0.5, max * 2];
            }
            const pad = min === 0 ? 1 : Math.abs(min) * 0.1;
            return [min - pad, max + pad];
        }

        if (isLog) {
            return [min * 0.9, max * 1.1];
        }

        const range = max - min;
        return [min - range * 0.1, max + range * 0.1];
    };

    const xDomain = isXNumeric ? calculateDomain('x') : undefined;
    const yDomain = calculateDomain('y');

    return (
        <div className="w-full h-full" style={animationStyle}>
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
            `}</style>
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                    data={dataWithTrend}
                    margin={{ top: 20, right: legend.show ? 120 : 30, bottom: 40, left: 60 }}
                >
                    <CartesianGrid
                        strokeDasharray={axisOptions.minorGrid ? "1 1" : "3 3"}
                        stroke={axisOptions.gridColor || '#333'}
                        strokeWidth={axisOptions.gridThickness || 1}
                        vertical={axisOptions.grid}
                        horizontal={axisOptions.grid}
                    />
                    <XAxis
                        type={isXNumeric ? 'number' : 'category'}
                        dataKey="x"
                        scale={isXNumeric && axisOptions.logScaleX ? 'log' : 'auto'}
                        domain={isXNumeric ? xDomain : undefined}
                        allowDataOverflow={true}
                        tick={{ fill: '#888', fontSize: 10 }}
                        tickFormatter={isXNumeric ? formatAxis : undefined}
                        stroke="#666"
                    >
                        <Label
                            value={axisOptions.labelX || fieldMappings.xField || 'X'}
                            position="bottom"
                            offset={15}
                            fill="#aaa"
                            fontSize={12}
                        />
                    </XAxis>
                    <YAxis
                        type="number"
                        scale={axisOptions.logScaleY ? 'log' : 'auto'}
                        domain={yDomain}
                        allowDataOverflow={true}
                        tick={{ fill: '#888', fontSize: 10 }}
                        tickFormatter={formatAxis}
                        stroke="#666"
                    >
                        <Label
                            value={axisOptions.labelY || fieldMappings.yField || 'Y'}
                            angle={-90}
                            position="left"
                            offset={25}
                            fill="#aaa"
                            fontSize={12}
                        />
                    </YAxis>
                    <Tooltip
                        contentStyle={{
                            backgroundColor: '#1a1a2e',
                            border: '1px solid #f59e0b33',
                            borderRadius: '8px',
                            color: '#fff',
                            boxShadow: '0 4px 20px rgba(245, 158, 11, 0.15)'
                        }}
                        cursor={{ strokeDasharray: '3 3', stroke: '#f59e0b' }}
                        formatter={(value: number, name: string) => [
                            axisOptions.axisFormat === 'scientific' ? value?.toExponential?.(4) ?? value : value?.toFixed?.(4) ?? value,
                            name
                        ]}
                    />
                    {legend.show && (
                        <Legend
                            layout="vertical"
                            align="right"
                            verticalAlign="middle"
                            wrapperStyle={{ paddingLeft: 20 }}
                        />
                    )}

                    {/* Confidence Interval Area (if provided) */}
                    {hasConfidenceInterval && (
                        <Area
                            type={style.curveSmoothing ? 'monotone' : 'linear'}
                            dataKey={(d: any) => [d.yLower, d.yUpper]}
                            stroke="none"
                            fill="#f59e0b"
                            fillOpacity={0.15}
                            name="95% CI"
                        />
                    )}

                    {/* Trendline */}
                    {hasTrendline && (
                        <Line
                            type="monotone"
                            dataKey="trend"
                            stroke="#ef4444"
                            strokeWidth={2}
                            strokeDasharray="5 5"
                            dot={false}
                            name="Trendline"
                            isAnimationActive={true}
                        />
                    )}

                    {/* Render lines for each series */}
                    {seriesKeys.map((seriesKey, idx) => {
                        const dataKey = seriesKeys.length > 1 ? `y_${seriesKey}` : 'y';
                        // Use colorField-based coloring if available, otherwise use series color
                        const baseColor = seriesColors[idx % seriesColors.length];

                        // Custom dot renderer for marker shapes
                        const renderDot = style.showPointMarkers ? (props: any) => {
                            const { cx, cy, payload } = props;
                            if (cx === undefined || cy === undefined) return null;

                            // Use color from colorField if available
                            const pointColor = fieldMappings.colorField && payload.colorNorm !== undefined
                                ? getColorFromPalette(payload.colorNorm)
                                : baseColor;

                            const size = style.markerSize / 2;
                            const shape = style.markerShape || 'circle';

                            if (shape === 'circle') {
                                return (
                                    <circle
                                        cx={cx} cy={cy} r={size}
                                        fill={pointColor}
                                        stroke={style.markerOutline ? '#fff' : 'none'}
                                        strokeWidth={style.markerOutline ? 1 : 0}
                                    />
                                );
                            } else if (shape === 'square') {
                                return (
                                    <rect
                                        x={cx - size} y={cy - size}
                                        width={size * 2} height={size * 2}
                                        fill={pointColor}
                                        stroke={style.markerOutline ? '#fff' : 'none'}
                                        strokeWidth={style.markerOutline ? 1 : 0}
                                    />
                                );
                            } else if (shape === 'triangle') {
                                const points = `${cx},${cy - size} ${cx + size},${cy + size} ${cx - size},${cy + size}`;
                                return (
                                    <polygon
                                        points={points}
                                        fill={pointColor}
                                        stroke={style.markerOutline ? '#fff' : 'none'}
                                        strokeWidth={style.markerOutline ? 1 : 0}
                                    />
                                );
                            } else if (shape === 'diamond') {
                                const points = `${cx},${cy - size} ${cx + size},${cy} ${cx},${cy + size} ${cx - size},${cy}`;
                                return (
                                    <polygon
                                        points={points}
                                        fill={pointColor}
                                        stroke={style.markerOutline ? '#fff' : 'none'}
                                        strokeWidth={style.markerOutline ? 1 : 0}
                                    />
                                );
                            } else if (shape === 'cross') {
                                return (
                                    <g>
                                        <line x1={cx - size} y1={cy - size} x2={cx + size} y2={cy + size} stroke={pointColor} strokeWidth={2} />
                                        <line x1={cx + size} y1={cy - size} x2={cx - size} y2={cy + size} stroke={pointColor} strokeWidth={2} />
                                    </g>
                                );
                            }
                            return <circle cx={cx} cy={cy} r={size} fill={pointColor || chartColor} />;
                        } : false;

                        return (
                            <React.Fragment key={seriesKey}>
                                <Line
                                    type={style.curveSmoothing ? 'monotone' : 'linear'}
                                    dataKey={dataKey}
                                    name={seriesKey === 'default' ? (fieldMappings.yField || 'Value') : seriesKey}
                                    stroke={baseColor}
                                    strokeWidth={style.lineWidth}
                                    strokeDasharray={getDashArray()}
                                    dot={renderDot}
                                    activeDot={{
                                        r: style.markerSize,
                                        fill: baseColor,
                                        stroke: '#fff',
                                        strokeWidth: 2
                                    }}
                                    opacity={style.alpha}
                                    isAnimationActive={animation.fadeIn}
                                    animationDuration={animation.duration}
                                />
                            </React.Fragment>
                        );
                    })}

                    {/* Error Bar Overlay using Scatter */}
                    {errorBars?.show && errorBars.mode !== 'none' && (
                        <Scatter
                            data={dataWithTrend.filter(d => d.errorY && (d.errorY[0] > 0 || d.errorY[1] > 0))}
                            dataKey="y"
                            fill="transparent"
                            isAnimationActive={false}
                        >
                            <ErrorBar
                                dataKey="errorY"
                                width={errorBars.capSize || 5}
                                strokeWidth={errorBars.lineThickness || 1}
                                stroke={errorBars.color || chartColor}
                                direction="y"
                            />
                        </Scatter>
                    )}
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
};

export default ScientificLinePlot;
